/**
 * Hook: slot management, filtering, and grouping for the unified timeline.
 * Shared across all TaskLoop agent views.
 */

import { useMemo, useState } from "react";
import { useThreadMessages } from "@convex-dev/agent/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  SubAgentInfo,
  TaskTreeNode,
  TimelineEntry,
} from "./timeline-types";
import { groupEntries } from "./timeline-helpers";

const MAX_SLOTS = 15;

function useFixedThreadSlot(threadId: string | undefined, jobId: string) {
  return useThreadMessages(
    api.jobs.summarization.publicQueries.listAgentThreadMessages,
    // `task.jobId` on the caller's side is a plain string (see the `task`
    // param type below); the query itself requires `Id<"jobs">`.
    threadId ? { threadId, jobId: jobId as Id<"jobs"> } : "skip",
    { initialNumItems: 50, stream: true },
  );
}

/** Truncate a worker description to a readable tab label. */
function shortenWorkerLabel(desc: string): string {
  if (desc.length <= 60) return desc;
  return desc.slice(0, 58) + "...";
}

export function useTimelineData(
  task: {
    threadId?: string;
    jobId: string;
    status: string;
    activityLog?: Array<{ timestamp: number; message: string }>;
  },
  subAgents: SubAgentInfo[],
  taskTree?: TaskTreeNode[],
) {
  // Convert taskTree worker nodes into SubAgentInfo format
  const taskTreeSubAgents: SubAgentInfo[] = useMemo(() => {
    if (!taskTree || taskTree.length === 0) return [];
    return taskTree
      .filter(
        (n): n is TaskTreeNode & { threadId: string } =>
          n.type === "worker" && !!n.threadId,
      )
      .map((n) => ({
        id: n.id,
        label: shortenWorkerLabel(n.description ?? `Worker ${n.id}`),
        threadId: n.threadId,
        status:
          n.status === "completed"
            ? "completed"
            : n.status === "failed"
              ? "failed"
              : "running",
        startedAt: n.startedAt ?? n.dispatchedAt ?? 0,
        completedAt: n.completedAt,
        error: n.error,
      }));
  }, [taskTree]);

  // Merge: explicit subAgents take priority, then taskTree workers
  const allSubAgents = useMemo(() => {
    if (subAgents.length > 0) return subAgents;
    return taskTreeSubAgents;
  }, [subAgents, taskTreeSubAgents]);

  const subAgentThreadIds = useMemo(() => {
    const mainThreadId = task.threadId;
    const subAgentIds = new Set(allSubAgents.map((sa) => sa.threadId));
    const includeMain = mainThreadId && !subAgentIds.has(mainThreadId);
    return { includeMain, mainThreadId };
  }, [task.threadId, allSubAgents]);

  const slotIds = useMemo(() => {
    const ids: (string | undefined)[] = [];
    ids.push(
      subAgentThreadIds.includeMain
        ? subAgentThreadIds.mainThreadId
        : undefined,
    );
    for (let i = 0; i < MAX_SLOTS - 1; i++) ids.push(allSubAgents[i]?.threadId);
    return ids;
  }, [subAgentThreadIds, allSubAgents]);

  const jid = task.jobId;
  const t0 = useFixedThreadSlot(slotIds[0], jid);
  const t1 = useFixedThreadSlot(slotIds[1], jid);
  const t2 = useFixedThreadSlot(slotIds[2], jid);
  const t3 = useFixedThreadSlot(slotIds[3], jid);
  const t4 = useFixedThreadSlot(slotIds[4], jid);
  const t5 = useFixedThreadSlot(slotIds[5], jid);
  const t6 = useFixedThreadSlot(slotIds[6], jid);
  const t7 = useFixedThreadSlot(slotIds[7], jid);
  const t8 = useFixedThreadSlot(slotIds[8], jid);
  const t9 = useFixedThreadSlot(slotIds[9], jid);
  const t10 = useFixedThreadSlot(slotIds[10], jid);
  const t11 = useFixedThreadSlot(slotIds[11], jid);
  const t12 = useFixedThreadSlot(slotIds[12], jid);
  const t13 = useFixedThreadSlot(slotIds[13], jid);
  const t14 = useFixedThreadSlot(slotIds[14], jid);
  const allThreadResults = [
    t0,
    t1,
    t2,
    t3,
    t4,
    t5,
    t6,
    t7,
    t8,
    t9,
    t10,
    t11,
    t12,
    t13,
    t14,
  ];

  const [hiddenSources, setHiddenSources] = useState<Set<string>>(new Set());
  const toggleSource = (source: string) => {
    setHiddenSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  const entries = useMemo(() => {
    const result: TimelineEntry[] = [];

    if (task.activityLog) {
      for (let i = 0; i < task.activityLog.length; i++) {
        result.push({
          type: "activity",
          timestamp: task.activityLog[i].timestamp,
          message: task.activityLog[i].message,
          key: `activity-${i}`,
        });
      }
    }

    for (const sa of allSubAgents) {
      if (sa.startedAt)
        result.push({
          type: "lifecycle",
          timestamp: sa.startedAt,
          subAgentId: sa.id,
          event: "started",
          label: sa.label,
          key: `lifecycle-${sa.id}-started`,
        });
      if (sa.completedAt && sa.status === "completed")
        result.push({
          type: "lifecycle",
          timestamp: sa.completedAt,
          subAgentId: sa.id,
          event: "completed",
          label: sa.label,
          entityCount: sa.entityCount,
          key: `lifecycle-${sa.id}-completed`,
        });
      if (sa.completedAt && sa.status === "failed")
        result.push({
          type: "lifecycle",
          timestamp: sa.completedAt,
          subAgentId: sa.id,
          event: "failed",
          label: sa.label,
          error: sa.error,
          key: `lifecycle-${sa.id}-failed`,
        });
    }

    for (let slotIdx = 0; slotIdx < MAX_SLOTS; slotIdx++) {
      const messages = allThreadResults[slotIdx]?.results;
      if (!messages) continue;
      const resolvedSource =
        slotIdx === 0 && subAgentThreadIds.includeMain
          ? { source: "main", sourceLabel: "Coordinator" }
          : slotIdx > 0 && allSubAgents[slotIdx - 1]
            ? {
                source: allSubAgents[slotIdx - 1].id,
                sourceLabel: allSubAgents[slotIdx - 1].label,
              }
            : null;
      if (!resolvedSource) continue;
      const { source, sourceLabel } = resolvedSource;

      for (const msg of messages) {
        const role = msg.message?.role;
        if (!role) continue;
        result.push({
          type: "agent_message",
          timestamp: msg._creationTime > 0 ? msg._creationTime : Date.now(),
          source,
          sourceLabel,
          message: msg,
          role,
          key: `msg-${source}-${msg.key}`,
          streaming: msg.streaming ?? false,
        });
      }
    }

    result.sort((a, b) => a.timestamp - b.timestamp);
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    task.activityLog,
    allSubAgents,
    subAgentThreadIds.includeMain,
    t0?.results,
    t1?.results,
    t2?.results,
    t3?.results,
    t4?.results,
    t5?.results,
    t6?.results,
    t7?.results,
    t8?.results,
    t9?.results,
    t10?.results,
    t11?.results,
    t12?.results,
    t13?.results,
    t14?.results,
  ]);

  const availableSources = useMemo(() => {
    const sources: Array<{ id: string; label: string; isMain: boolean }> = [];
    if (subAgentThreadIds.includeMain)
      sources.push({ id: "main", label: "Coordinator", isMain: true });
    for (const sa of allSubAgents)
      sources.push({ id: sa.id, label: sa.label, isMain: false });
    sources.push({ id: "activity", label: "Activity", isMain: false });
    return sources;
  }, [subAgentThreadIds.includeMain, allSubAgents]);

  const filteredEntries = useMemo(() => {
    if (hiddenSources.size === 0) return entries;
    return entries.filter((entry) => {
      if (entry.type === "activity") return !hiddenSources.has("activity");
      if (entry.type === "lifecycle") return !hiddenSources.has("lifecycle");
      return !hiddenSources.has(entry.source);
    });
  }, [entries, hiddenSources]);

  const groupedItems = useMemo(
    () => groupEntries(filteredEntries),
    [filteredEntries],
  );

  return {
    entries,
    groupedItems,
    availableSources,
    hiddenSources,
    toggleSource,
  };
}
